import { useEffect, useState } from 'react';
import { Container, Image, SelectField, SideBarCategory } from '@doist/marketist';
import cn from 'clsx';
import categoryIconMapping from 'components/help/utils/category-icon-mapping';
import { useTranslation } from 'next-i18next';
import ArticlesGrid from '../article-grid/article-grid';
import css from './main-section.module.css';
import type { PartialCategory, PartialSection } from '@doist/help-center-utils';
const ArticleGridId = 'article-grid-id';
export type MainSectionProps = {
  categories: PartialCategory[];
  category: PartialCategory;
  section: PartialSection;
};
export default function MainSection({
  categories,
  category,
  section
}: MainSectionProps) {
  const {
    i18n
  } = useTranslation('helpcenter');
  const [defaultSection] = category.sections;
  const [currentCategory, setCurrentCategory] = useState(category);
  const [currentSection, setCurrentSection] = useState(section || defaultSection);
  const [sideBarExpanded, setSideBarExpanded] = useState<number | null>(category.id);
  function onSelectCategory(selectedCategoryId: PartialCategory['id']) {
    const selectedCategory = categories.find(c => c.id === selectedCategoryId);
    if (!selectedCategory || currentCategory.id === selectedCategory.id) {
      return;
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setCurrentCategory(selectedCategory);
    const [newSection] = selectedCategory.sections;
    setCurrentSection(newSection);
  }
  function onSelectSection(selectedSectionId: PartialSection['id']) {
    const selectedSection = currentCategory.sections.find(c => c.id === selectedSectionId);
    if (!selectedSection || currentSection.id === selectedSection.id) {
      return;
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setCurrentSection(selectedSection);
  }
  useEffect(() => {
    /** Updates the browser route to have an easier way to share a category or section. */
    const langPrefix = i18n.language !== 'en' ? `/${i18n.language}` : '';
    window.history.replaceState(`category-${currentCategory.id}`, currentCategory.name, langPrefix + currentSection.href);
  }, [i18n.language, currentCategory, currentSection]);
  return <Container tag="section" className={cn(css.mainSection, 'pbe-96 md-pbe-128')} data-sentry-element="Container" data-sentry-component="MainSection" data-sentry-source-file="main-section.tsx">
            <MobileNavigation categories={categories} currentCategory={currentCategory} currentSection={currentSection} onSelectCategory={onSelectCategory} onSelectSection={onSelectSection} data-sentry-element="MobileNavigation" data-sentry-source-file="main-section.tsx" />
            <DesktopNavigation categories={categories} currentCategory={currentCategory} currentSection={currentSection} onSelectCategory={onSelectCategory} onSelectSection={onSelectSection} sideBarExpanded={sideBarExpanded} setSideBarExpanded={setSideBarExpanded} data-sentry-element="DesktopNavigation" data-sentry-source-file="main-section.tsx" />
            <ArticlesGrid id={ArticleGridId} category={currentCategory} currentSection={currentSection} data-sentry-element="ArticlesGrid" data-sentry-source-file="main-section.tsx" />
        </Container>;
}
type NavigationProps = {
  categories: PartialCategory[];
  currentCategory: PartialCategory;
  currentSection: PartialSection;
  onSelectCategory: (categoryId: PartialCategory['id']) => void;
  onSelectSection: (sectionId: PartialSection['id']) => void;
  sideBarExpanded?: number | null;
  setSideBarExpanded?: (id: number | null) => void;
};
function MobileNavigation({
  categories,
  currentCategory,
  currentSection,
  onSelectCategory,
  onSelectSection
}: NavigationProps) {
  const {
    t
  } = useTranslation('helpcenter');
  return <nav className={css.mobile} data-sentry-component="MobileNavigation" data-sentry-source-file="main-section.tsx">
            <Image className="mbe-8" width={223} height={159} alt="" src={`${process.env.NEXT_PUBLIC_BASE_URL}/static/help/side-bar.png`} priority data-sentry-element="Image" data-sentry-source-file="main-section.tsx" />

            <SelectField id="category-dropdown" label={t('category')} value={currentCategory.id} size="sm" onChange={e => onSelectCategory(Number(e.currentTarget.value) as PartialCategory['id'])} data-sentry-element="SelectField" data-sentry-source-file="main-section.tsx">
                {categories.map(category => <option key={category.id} value={category.id}>
                        {category.name}
                    </option>)}
            </SelectField>

            <SelectField id="section-dropdown" label={t('section')} value={currentSection.id} size="sm" onChange={e => onSelectSection(Number(e.currentTarget.value) as PartialSection['id'])} data-sentry-element="SelectField" data-sentry-source-file="main-section.tsx">
                {currentCategory.sections.map(section => <option key={section.id} value={section.id}>
                        {section.name}
                    </option>)}
            </SelectField>
        </nav>;
}
function DesktopNavigation({
  categories,
  currentCategory,
  currentSection,
  onSelectCategory,
  onSelectSection,
  sideBarExpanded,
  setSideBarExpanded
}: NavigationProps) {
  const {
    t
  } = useTranslation('helpcenter');
  return <aside className={css.desktop} data-sentry-component="DesktopNavigation" data-sentry-source-file="main-section.tsx">
            <Image className="mbe-32" width={223} height={159} src={`${process.env.NEXT_PUBLIC_BASE_URL}/static/help/side-bar.png`} alt="" priority data-sentry-element="Image" data-sentry-source-file="main-section.tsx" />
            <ul>
                {categories.map(cat => <SideBarCategory key={cat.id} tag="li" name={cat.name} expanded={sideBarExpanded === cat.id} icon={categoryIconMapping[cat.id].icon.small} links={cat.sections.map(s => {
        if (s.id === 0) {
          return t('allIntegrations');
        }
        return s.name;
      })} activeLinkIndex={cat.id === currentCategory.id ? cat.sections.findIndex(s => s.id === currentSection.id) : undefined} onChange={(index: number) => onSelectSection(cat.sections[index].id)} onCategoryClick={() => {
        setSideBarExpanded?.(cat.id === sideBarExpanded ? null : cat.id);
        onSelectCategory(cat.id);
      }} />)}
            </ul>
        </aside>;
}